/**
 * aethereum rotate — rotate this project's room key from the CLI.
 *
 * Calls POST /api/rooms/[id]/rotate with the project's room token. The server
 * revokes every existing room token (so a leaked key dies), mints a replacement
 * for THIS machine's member seat, and returns it once; we then re-wire the
 * project configs (config.json, .mcp.json, editor configs) with the new token
 * via the same wireProject init uses. Prints the room's join code so teammates
 * can rejoin with `aethereum join <code>`.
 *
 * Fails soft: not wired / offline / not authorized prints a clear message and
 * exits 1, never crashes.
 */

import { homedir } from "node:os";
import { resolveProject } from "../hive/config-store.js";
import { wireProject } from "./init.js";

export interface RotateOptions {
  url?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

export async function runRotate(options: RotateOptions = {}): Promise<void> {
  const home = options.home ?? homedir();
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const project = resolveProject(cwd, home);

  const base = (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? project?.url ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");

  if (!project?.token) {
    console.error("No room wired here. Run 'aethereum init' first, then 'aethereum rotate'.");
    process.exit(1);
    return;
  }
  if (!project.roomId) {
    console.error(
      "This room's id is unknown locally (older config). Run 'aethereum reconnect' to recover it, then 'aethereum rotate'.",
    );
    process.exit(1);
    return;
  }

  let res: Response;
  try {
    res = await (options.fetchImpl ?? fetch)(
      `${base}/api/rooms/${encodeURIComponent(project.roomId)}/rotate`,
      { method: "POST", headers: { Authorization: `Bearer ${project.token}` } },
    );
  } catch {
    console.error("Could not reach Aethereum (offline?). The key was NOT rotated; try again when you are online.");
    process.exit(1);
    return;
  }

  if (res.status === 401 || res.status === 403) {
    console.error(
      "Not authorized to rotate this room with the stored token. If it was already rotated elsewhere, reconnect from the dashboard (Connect) and re-run 'aethereum init --token <new-token>'.",
    );
    process.exit(1);
    return;
  }
  if (!res.ok) {
    console.error(`Rotate failed (${res.status}). The key may not have been rotated; check the dashboard.`);
    process.exit(1);
    return;
  }

  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    revoked?: number;
    token?: string;
    joinCode?: string | null;
  };

  console.log(`\n  [ok] room key rotated (${body.revoked ?? 0} old token(s) revoked).`);

  if (!body.token) {
    console.log(
      "  The server did not return a replacement token, so this machine must reconnect:\n" +
        `    open ${base}/dashboard, Connect to the room, then 'aethereum init --token <new-token>'.\n`,
    );
    return;
  }

  // Re-wire THIS project with the replacement token (same writers as init).
  const wired = wireProject(cwd, home, base, body.token, env, {
    roomId: project.roomId,
    anon: project.anon,
    joinCode: body.joinCode ?? project.joinCode,
  });
  console.log("\n  Re-wired this project with the new key:");
  for (const line of wired.summary) console.log(line);

  const joinCode = body.joinCode ?? project.joinCode;
  if (joinCode) {
    console.log(`\n  Teammates' old tokens are dead. Bring them back in, they run:`);
    console.log(`    npx aethereum join ${joinCode}\n`);
  } else {
    console.log("\n  Teammates' old tokens are dead; they reconnect from the dashboard (Connect).\n");
  }
}
